import React from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import type AssistantMessage from '../../../framework/models/AssistantMessage';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import MessageAttachment from './MessageAttachment';

interface UserMessageScopeProps {
  message: AssistantMessage;
}

const UserMessageScope: React.FC<UserMessageScopeProps> = ({ message }) => {
  const controller = useController<AssistantViewController>();
  const { instructions } = controller.state;

  const nonImageAttachments = message.attachments?.filter(
    a => !a.contentType?.startsWith('image/')
  );

  if (!message.scope || typeof message.scope !== 'object') {
    return null;
  }

  const scope = message.scope;
  const scopeItems: React.ReactNode[] = [];

  // Handle non-image attachments
  if (nonImageAttachments && nonImageAttachments.length > 0) {
    nonImageAttachments.forEach((attachment, index) => {
      scopeItems.push(
        <MessageAttachment key={attachment.uniqueId} attachment={attachment} />
      );
    });
  }

  // Handle instructions
  if (
    scope.instructions &&
    Array.isArray(scope.instructions) &&
    scope.instructions.length > 0
  ) {
    scope.instructions.forEach((instructionId: string, index: number) => {
      // Find the instruction name from controller state
      const instruction = instructions?.find(inst => inst.id === instructionId);
      const instructionName = instruction?.name || `Instruction`;

      scopeItems.push(
        <div
          key={`instruction-${index}`}
          className={styles.userMessageScopeItem}
        >
          {instructionName}
        </div>
      );
    });
  }

  // Handle references
  if (
    scope.references &&
    Array.isArray(scope.references) &&
    scope.references.length > 0
  ) {
    scope.references.forEach((reference: string, index: number) => {
      const url = `/records/${reference}`;

      scopeItems.push(
        <div key={`reference-${index}`} className={styles.userMessageScopeItem}>
          <a
            href={url}
            data-drawer-url={url}
            className={styles.assistantReference}
          >
            <aha-record-icon reference-num={reference} /> {reference}
          </a>
        </div>
      );
    });
  }

  if (scopeItems.length === 0) {
    return null;
  }

  return <div className={styles.userMessageScope}>{scopeItems}</div>;
};

export default ApplicationView(UserMessageScope);
